import styled from 'styled-components';
import Colours from '@shared/ui/atoms/Colours';
import { BodySmall, BodyVerySmall } from '@shared/ui/atoms/fonts/Body';
import { FreeBusyStatus, WhereaboutsOption } from '@features/whereabouts/services/WhereaboutOptions';

// Accent colours specific to the whereabouts v2 design; not part of the shared palette.
export const V2Colours = {
  navy: '#1A2167',
  indigo: '#515BB0',
  lightIndigo: '#B2CDFF',
  paleCyan: '#E6FCFF',
};

const COLOUR_BG_MAP: Record<string, string> = {
  lightgreen: '#D5F0C8',
  green: '#C5E8B4',
  yellow: '#FFF3CD',
  grey: '#E8E8E8',
  red: '#FFCDD2',
};

function getCellBg(colour: string): string {
  return COLOUR_BG_MAP[colour?.toLowerCase()] || '#E8E8E8';
}

export function getCellColours(option: WhereaboutsOption): { bg: string; border: string } {
  if (option.key === 'UNKNOWN') return { bg: '#FFFFFF', border: '#BCC6C7' };
  if (option.status === 'OFFICE') return { bg: '#E3FFAB', border: '#4DCD67' };
  if (option.freeBusyStatus === FreeBusyStatus.OOF) return { bg: Colours.iconGrey, border: Colours.mildGrey };
  if (option.freeBusyStatus === FreeBusyStatus.WORKING_ELSEWHERE) return { bg: '#FFF6A1', border: '#FFD027' };
  if (option.freeBusyStatus === FreeBusyStatus.FREE) return { bg: '#BDFAC8', border: '#4DCD67' };
  return { bg: getCellBg(option.colour), border: 'transparent' };
}

export const TeamSection = styled.div`
  padding-top: 16px;
`;

export const TeamHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 16px;
  .iconButton__button {
    margin-left: auto;
  }
`;

export const UserRow = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 4px;
  padding: 4px 0;
  min-height: 50px;
`;

export const Cell = styled.div<{ $bg: string; $borderColour: string }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0px 8px;
  background: ${props => props.$bg};
  border-radius: 6px;
  border: 1px solid ${props => props.$borderColour};
  overflow: hidden;
`;

export const CellIcon = styled.img`
  height: 75%;
  max-width: 40px;
  max-height: 40px;
  flex-shrink: 0;
`;

export const CellText = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  overflow: hidden;
  p {
    line-height: normal;
  }
`;

export const DeskLabel = styled(BodyVerySmall)`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const NoteIndicator = styled.div`
  margin-left: auto;
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  padding: 4px;
  box-sizing: border-box;
  border-radius: 50%;
  background: ${Colours.white};
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const Container = styled.div`
  position: relative;
  min-height: 100%;
`;

export const ColumnBackgrounds = styled.div<{ $columns: number }>`
  position: absolute;
  inset: 0;
  display: grid;
  grid-template-columns: repeat(${props => props.$columns}, 1fr);
  pointer-events: none;
`;

export const ColumnBg = styled.div<{ $even: boolean; $isLast: boolean }>`
  background: ${props => props.$even ? 'white' : Colours.veryLightGrey};
  border-left: 1px solid ${Colours.lightGrey};
  ${props => props.$isLast && `border-right: 1px solid ${Colours.lightGrey};`}
`;

export const ContentLayer = styled.div`
  position: relative;
  &:last-child {
    padding-bottom: 600px;
  }
`;

export const TeamSelectSection = styled.div`
  padding: 16px 16px 0;
  max-width: 500px;
  .multiSelect__dropDown {
    min-width: 350px;
    & > div {
      min-width: 350px;
    }
  }
`;

export const RestrictedMessage = styled.div`
  padding: 12px 16px;
`;

const RestrictedCardMessage = styled.div`
  padding: 12px;
  background: white;
  border-radius: 8px;
`;

export const EmptyState = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 48px;
  color: ${Colours.darkGrey};
`;

export const EmptyTeamMessage = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
`;

export const ShowAllButton = styled.button`
  background: transparent;
  border: 1px solid ${Colours.blue};
  color: ${Colours.blue};
  border-radius: 4px;
  padding: 4px 12px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  &:hover {
    background: rgba(0, 0, 0, 0.02);
  }
`;

export const MemberWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

export const SavingClickableCell = styled(Cell)<{ $saving?: boolean }>`
  cursor: ${props => props.$saving ? 'default' : 'pointer'};
  position: relative;
`;

export const SavingOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.7);
`;

const GroupSection = styled.div`
  margin-bottom: 32px;
`;

const GroupSectionHeader = styled.div`
  padding: 12px 0;
  border-bottom: 1px solid ${Colours.mildGrey};
  margin-bottom: 16px;
`;
